/**
 * Directive `v-overlay-close` — fermer une modale par le fond SANS la fermer
 * quand le relachement de la souris atterrit la par accident.
 *
 * 🚨 LE DEFAUT QU'ELLE CORRIGE (recette du 2026-10-25, story DS-1.23, AC9).
 * Un evenement `click` n'est pas dispatche sur la cible du relachement : il est
 * dispatche sur l'ANCETRE COMMUN du `mousedown` et du `mouseup`. Selectionner
 * le texte d'un `<input>` en faisant sortir le curseur du cadre produit donc un
 * `click` dont la cible est L'OVERLAY — et `@click.self="close"` fermait la
 * modale, la saisie en cours perdue. Meme chose pendant un glisser de
 * selection, un glisser de curseur, ou le redimensionnement d'un `<textarea>`.
 *
 * LA REGLE : le gestionnaire n'est appele que si L'APPUI *ET* LE RELACHEMENT
 * ont eu lieu sur l'overlay lui-meme. Rien d'autre ne change — le clic franc
 * sur le fond ferme toujours, et `Echap` n'est pas concerne (il est gere
 * ailleurs, par composant ou par `utils/modalFocus.js`).
 *
 * EMPLOI — la valeur doit etre UNE FONCTION. Un `@click.self` portant une
 * instruction en ligne se reecrit donc avec une fleche, sinon l'expression
 * serait evaluee au rendu :
 *
 *   <div class="zi-modal-overlay" v-overlay-close="close">
 *   <div class="zi-modal-overlay" v-overlay-close="() => $emit('close')">
 *   <div class="zi-modal-overlay" v-overlay-close="() => (showConfirm = false)">
 *
 * ⛔ Une modale qui refuse de fermer pendant une operation longue continue de
 * refuser : son propre gestionnaire (`requestClose`, prop `loading`…) est
 * conserve tel quel, seule la CONDITION DE DECLENCHEMENT change.
 *
 * ⚠ Les clics purement programmatiques (`el.click()`) ne declenchent rien :
 * sans `mousedown` ni `mouseup`, la garde n'est pas armee. C'est voulu.
 */
const STATE = new WeakMap()

function attach(el, handler) {
  const st = { handler, downOnSelf: false, upOnSelf: false }

  st.onMouseDown = (event) => {
    // Un nouvel appui rearme a zero : un `mouseup` perdu hors de la fenetre ne
    // laisse donc jamais d'etat rance derriere lui.
    st.downOnSelf = event.target === el
    st.upOnSelf = false
  }
  st.onMouseUp = (event) => {
    st.upOnSelf = event.target === el
  }
  st.onClick = (event) => {
    const fromOverlay = st.downOnSelf && st.upOnSelf && event.target === el
    st.downOnSelf = false
    st.upOnSelf = false
    if (fromOverlay && typeof st.handler === 'function') {
      st.handler(event)
    }
  }

  el.addEventListener('mousedown', st.onMouseDown)
  el.addEventListener('mouseup', st.onMouseUp)
  el.addEventListener('click', st.onClick)
  STATE.set(el, st)
}

function detach(el) {
  const st = STATE.get(el)
  if (!st) return
  el.removeEventListener('mousedown', st.onMouseDown)
  el.removeEventListener('mouseup', st.onMouseUp)
  el.removeEventListener('click', st.onClick)
  STATE.delete(el)
}

export const overlayClose = {
  mounted(el, binding) {
    attach(el, binding.value)
  },
  updated(el, binding) {
    // Le gabarit recree la fonction a chaque rendu : on remplace la reference
    // sans toucher aux ecouteurs ni a l'etat du geste en cours.
    const st = STATE.get(el)
    if (st) st.handler = binding.value
  },
  unmounted(el) {
    detach(el)
  }
}

export default overlayClose
